import { CanvasNode, CanvasEdge } from '../canvasStore';
import { Track, useTrackStore } from '../trackStore';

export interface FilteredCanvasElements {
  nodes: Map<string, CanvasNode>;
  edges: Map<string, CanvasEdge>;
}

/**
 * Filter nodes and edges based on current level
 */
export function filterByLevel(
  allNodes: Map<string, CanvasNode>,
  allEdges: Map<string, CanvasEdge>,
  currentLevelName: string,
  tracks?: Map<string, Track>
): FilteredCanvasElements {
  let tracksToUse: Map<string, Track>;
  tracksToUse = tracks || useTrackStore.getState().tracks;

  const visibleNodeIds = new Set<string>();
  const visibleTrackNames = new Set<string>();
  const isStartLevel = currentLevelName === 'start';
  const filteredNodes = new Map<string, CanvasNode>(allNodes);
  const filteredEdges = new Map<string, CanvasEdge>(allEdges);

  allNodes.forEach((node, id) => {
    if (isStartLevel && (id === 'trigger' || id === 'starter-node')) {
      visibleNodeIds.add(id);
    }
  });

  if (!isStartLevel) {
    const startTrackNode = allNodes.get('start-track');
    const startTrackEdgeId = allEdges.get(`start-track:${currentLevelName}`);

    const currentTrack = tracksToUse.get(currentLevelName);
    const firstComponentId = currentTrack?.componentIds[0];

    if (!startTrackNode) {
      const newStartTrackNode: CanvasNode = {
        id: 'start-track',
        position: { x: 0, y: 0 },
        tags: ['start-track'],
        deletable: false
      };

      filteredNodes.set('start-track', newStartTrackNode);
    }

    if (!startTrackEdgeId && firstComponentId) {
      const startTrackEdge: CanvasEdge = {
        id: `start-track:${firstComponentId}`,
        sourceId: 'start-track',
        targetId: firstComponentId,
        tags: ['default'],
        deletable: false
      };

      filteredEdges.set(startTrackEdge.id, startTrackEdge);
    }

    visibleNodeIds.add('start-track');
  }

  tracksToUse.forEach(track => {
    if (track.name === currentLevelName) {
      track.componentIds.forEach(id => visibleNodeIds.add(id));
      visibleTrackNames.add(track.name);
      return;
    }

    if (
      track.isDisconnected &&
      track.name.startsWith(`disconnected-${currentLevelName}:`)
    ) {
      track.componentIds.forEach(id => visibleNodeIds.add(id));
      visibleTrackNames.add(track.name);
    }
  });

  let tracksAdded = true;
  while (tracksAdded) {
    tracksAdded = false;

    tracksToUse.forEach(track => {
      if (visibleTrackNames.has(track.name)) return;

      if (
        track.parentComponentId &&
        (track.type === 'choice-when' ||
          track.type === 'choice-otherwise' ||
          track.type === 'parallel-execution')
      ) {
        if (visibleNodeIds.has(track.parentComponentId)) {
          track.componentIds.forEach(id => visibleNodeIds.add(id));
          visibleTrackNames.add(track.name);
          tracksAdded = true;
        }
      }
    });
  }

  const finalNodes = new Map<string, CanvasNode>();
  visibleNodeIds.forEach(id => {
    const node = filteredNodes.get(id);
    if (node) {
      finalNodes.set(id, node);
    }
  });

  const finalEdges = new Map<string, CanvasEdge>();
  filteredEdges.forEach((edge, edgeId) => {
    if (
      visibleNodeIds.has(edge.sourceId) &&
      visibleNodeIds.has(edge.targetId)
    ) {
      finalEdges.set(edgeId, edge);
    }
  });

  return {
    nodes: finalNodes,
    edges: finalEdges
  };
}
